import PropTypes from 'prop-types';
import { useCallback } from 'react';

// material-ui
import { Box } from '@mui/material';
import { GridActionsCellItem } from '@mui/x-data-grid-pro';
// import { LicenseInfo } from '@mui/x-license-pro';

// third-party
import { useIntl } from 'react-intl';

// project imports
import { useSelector } from 'store';
import AppDataGrid, { AppDataGridLocales, StyledDataGrid } from '../..';
import RenderGridCell from 'ui-component/renders/RenderGridCell';
import CustomDetailPanelToggle from './CustomDetailPanelToggle';

// LicenseInfo.setLicenseKey(process.env.REACT_APP_DATAGRIDPRO_LICENSE);

// ==============================|| APP DATA GRID - ENTITY SUBGRID ||============================== //

const bindOnClickActionsToParentRow = (onClickActions = {}, parentRow = null) =>
    Object.fromEntries(
        Object.entries(onClickActions || {}).map(([actionName, actionHandler]) => [
            actionName,
            (...args) => {
                if (typeof actionHandler === 'function') {
                    actionHandler(...args, parentRow);
                }
            }
        ])
    );

const AppDataGridEntitySubGrid = ({
    mainEntityName = '',
    mainEntityPreffix,
    mainEntitySuffix,
    secondaryEntityName = '',
    // eslint-disable-next-line no-unused-vars
    secondaryEntityPreffix = '',
    // eslint-disable-next-line no-unused-vars
    secondaryEntitySuffix = '',
    mainEntityLabels = {},
    // eslint-disable-next-line no-unused-vars
    secondaryEntityLabels = {},
    mainEntitySelectionActions = {},
    mainEntityPermissionData = [],
    secondaryEntityPermissionData = [],
    mainEntityFilterActions = {},
    mainEntityQuickSearchActions = {},
    mainEntitySortingActions = {},
    mainEntityPaginationActions = {},
    mainEntityExportActions = {},
    mainEntityImportActions = {},
    mainEntityOnClickActions = {},
    secondaryEntityOnClickActions = {},
    useLinkField = null,
    secondaryEntityGetRowId,
    ...others
}) => {
    const intl = useIntl();

    const {
        colDef: secondaryEntityColDef,
        dataGridState: secondaryEntityDataGridState,
        list: secondaryEntityList,
        isLoading: secondaryEntityIsLoading
        // limit: secondaryEntityLimit,
        // sortingDef: secondaryEntitySortingDef
    } = useSelector((state) => state[secondaryEntityName] || {});

    const buildSecondaryEntityColumns = useCallback(
        (boundSecondaryEntityOnClickActions = {}) =>
            secondaryEntityColDef?.map((col) => {
                if (col?.field === '__detail_panel_toggle__') return col;

                const newCol = { ...col, headerName: intl.formatMessage({ id: col?.label }) };

                if (col?.type === 'actions') {
                    Object.assign(newCol, {
                        getActions: (params) =>
                            col
                                ?.getActions(params, secondaryEntityPermissionData, boundSecondaryEntityOnClickActions)
                                ?.sort((a1, a2) => (a1?.position > a2?.position ? 1 : -1))
                                ?.map((action) => <GridActionsCellItem {...action} label={intl.formatMessage({ id: action?.label })} />)
                    });
                } else if ('renderCell' in (col || {}) && typeof col.renderCell === 'function') {
                    Object.assign(newCol, {
                        renderCell: (params) => col.renderCell(params, secondaryEntityPermissionData, boundSecondaryEntityOnClickActions)
                    });
                } else if (!('renderCell' in (col || {})) && !('valueFormatter' in (col || {})) && !('valueGetter' in (col || {}))) {
                    Object.assign(newCol, { renderCell: (params) => <RenderGridCell {...params} /> });
                }

                return newCol;
            }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [secondaryEntityColDef, secondaryEntityPermissionData]
    );

    const getDetailPanelHeight = useCallback(() => 'auto', []);
    const getDetailPanelContent = useCallback(
        ({ row }) => {
            const onSecondaryEntityCellDoubleClick = (params) => {
                if (
                    !params?.row?.deletedAt &&
                    secondaryEntityPermissionData?.includes('canEdit') &&
                    'onClickRowActionEdit' in (secondaryEntityOnClickActions || {}) &&
                    typeof secondaryEntityOnClickActions.onClickRowActionEdit === 'function'
                ) {
                    secondaryEntityOnClickActions.onClickRowActionEdit(params?.row);
                } else if (
                    secondaryEntityPermissionData?.includes('canView') &&
                    'onClickRowActionView' in (secondaryEntityOnClickActions || {}) &&
                    typeof secondaryEntityOnClickActions.onClickRowActionView === 'function'
                ) {
                    secondaryEntityOnClickActions.onClickRowActionView(params?.row);
                }
            };
            const getSecondaryEntityRowClassName = (params) => {
                if (params?.row?.state === 'canceled') {
                    return 'entity-status-canceled';
                }
                if (params?.row?.deletedAt) {
                    return 'entity-status-deleted';
                }

                return '';
            };
            const boundSecondaryEntityOnClickActions = bindOnClickActionsToParentRow(secondaryEntityOnClickActions, row);
            const detailColumns = buildSecondaryEntityColumns(boundSecondaryEntityOnClickActions);
            const detailRows = useLinkField && useLinkField in (row || {}) ? row[useLinkField] || [] : secondaryEntityList || [];

            return (
                <Box sx={{ py: 1, px: 0.5 }}>
                    <StyledDataGrid
                        rows={detailRows}
                        columns={detailColumns}
                        loading={secondaryEntityIsLoading}
                        density="compact"
                        hideFooter
                        onCellDoubleClick={onSecondaryEntityCellDoubleClick}
                        disableColumnFilter
                        disableColumnMenu
                        disableColumnPinning
                        disableColumnReorder
                        disableColumnResize
                        initialState={secondaryEntityDataGridState}
                        getRowId={secondaryEntityGetRowId}
                        // onSortModelChange={(model) => batch(() => {})}
                        getRowClassName={getSecondaryEntityRowClassName}
                        localeText={AppDataGridLocales}
                    />
                </Box>
            );
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            buildSecondaryEntityColumns,
            secondaryEntityDataGridState,
            secondaryEntityIsLoading,
            secondaryEntityList,
            secondaryEntityGetRowId,
            secondaryEntityOnClickActions,
            secondaryEntityPermissionData,
            useLinkField
        ]
    );

    return (
        <AppDataGrid
            entityName={mainEntityName}
            entityPreffix={mainEntityPreffix}
            entitySuffix={mainEntitySuffix}
            entityLabels={mainEntityLabels}
            selectionActions={mainEntitySelectionActions}
            permissionData={mainEntityPermissionData}
            filterActions={mainEntityFilterActions}
            quickSearchActions={mainEntityQuickSearchActions}
            sortingActions={mainEntitySortingActions}
            paginationActions={mainEntityPaginationActions}
            exportActions={mainEntityExportActions}
            importActions={mainEntityImportActions}
            onClickActions={mainEntityOnClickActions}
            rowThreshold={0}
            getDetailPanelHeight={getDetailPanelHeight}
            getDetailPanelContent={getDetailPanelContent}
            {...others}
        />
    );
};

AppDataGridEntitySubGrid.propTypes = {
    mainEntityName: PropTypes.string.isRequired,
    mainEntityPreffix: PropTypes.string,
    mainEntitySuffix: PropTypes.string,
    secondaryEntityName: PropTypes.string.isRequired,
    secondaryEntityPreffix: PropTypes.string,
    secondaryEntitySuffix: PropTypes.string,
    mainEntityLabels: PropTypes.object,
    secondaryEntityLabels: PropTypes.object,
    mainEntitySelectionActions: PropTypes.object,
    mainEntityPermissionData: PropTypes.arrayOf(PropTypes.string),
    secondaryEntityPermissionData: PropTypes.arrayOf(PropTypes.string),
    mainEntityFilterActions: PropTypes.object,
    mainEntityQuickSearchActions: PropTypes.object,
    mainEntitySortingActions: PropTypes.object,
    mainEntityPaginationActions: PropTypes.object,
    mainEntityExportActions: PropTypes.object,
    mainEntityImportActions: PropTypes.object,
    mainEntityOnClickActions: PropTypes.object,
    secondaryEntityOnClickActions: PropTypes.object,
    useLinkField: PropTypes.string,
    secondaryEntityGetRowId: PropTypes.func
};

export default AppDataGridEntitySubGrid;
export { CustomDetailPanelToggle };
